import React from 'react';
import { Comment } from '../types';
import { getPseudoColor } from '../utils/colorUtils';

interface ProgressBarProps {
  played: number;
  duration: number;
  comments: Comment[];
  onSeek: (fraction: number) => void;
  height?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  played,
  duration,
  comments,
  onSeek,
  height = '6px',
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const fraction = x / rect.width;
    
    onSeek(Math.max(0, Math.min(1, fraction)));
  };

  // Calculer les positions et couleurs des commentaires
  const commentMarkers = comments
    .filter(c => c.timestamp !== undefined && duration > 0)
    .map(c => ({
      position: ((c.timestamp || 0) / duration) * 100,
      color: getPseudoColor(c.authorPseudo || c.user?.name || 'anonymous'),
      timestamp: c.timestamp || 0
    }));

  return (
    <div className="w-full px-2 py-2">
      <div
        ref={containerRef}
        onClick={handleClick}
        className="relative w-full bg-gray-300 rounded-full cursor-pointer group hover:bg-gray-400 transition-colors"
        style={{ height }}
      >
        {/* Barre de progression */}
        <div
          className="bg-[#E25C48] rounded-full h-full transition-all"
          style={{ width: `${played * 100}%` }}
        />

        {/* Indicateurs de commentaires */}
        {commentMarkers.map((marker, index) => (
          <div
            key={index}
            className="absolute top-1/2 transform -translate-y-1/2 w-3 h-3 rounded-full shadow-md cursor-pointer hover:scale-125 transition-all border-2 border-white"
            style={{
              left: `${marker.position}%`,
              backgroundColor: marker.color,
            }}
            title={`Comment at ${Math.floor(marker.timestamp)}s`}
          />
        ))}
      </div>
    </div>
  );
};

export default ProgressBar;
